import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import SimpleSlider from '../Home/slider';
import { getTestimonials, addBusinessPartner } from '../../data/API';
import { Form, Input, Button, Collapse, message } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import './index.css';

const { Panel } = Collapse;

export default function Business(props) {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  props.setFooteroffice(true);
  props.setshowHideFooter(false);

  const toggleFAQ = (key) => {
    setActiveKey(activeKey === key ? null : key);
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);

    getTestimonials('business')
      .then((res) => {
        if (res && res.data) {
          setTestimonials(res.data);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  const handlePartnerSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const formData = {
        first_name: values.firstName,
        last_name: values.lastName,
        company_name: values.companyName,
        industry_type: values.industryType,
        email: values.email
      };

      const response = await addBusinessPartner(formData);

      if (response && response.status === 201) {
        message.success('Thank you! Your business partner application has been submitted successfully. We will contact you soon.');
        form.resetFields();
      } else {
        message.success('Your application has been submitted successfully!');
        form.resetFields();
      }
    } catch (error) {
      console.error('Error submitting partner form:', error);
      message.error('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const formSection = document.querySelector('.business-partner-form-section');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <title>BlueBox for Business | Sustainable Moving Solutions for Companies</title>
        <meta
          name="description"
          content="BlueBox provides eco-friendly, cost-effective moving and storage solutions for businesses. From office relocations to inventory management, we make it easy."
        />
        <meta
          name="keywords"
          content="business moving boxes, office relocation, corporate moving solutions, business storage, sustainable business moving"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="business-hero-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/img/bluebox-buisness.jpg)` }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h1 className="business-hero-title">BlueBox for Business</h1>
            </div>
          </div>
        </div>
      </section>
      {/* Hero Section End */}

      {/* How BlueBox Helps Businesses Section */}
      <section className="business-helps-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="business-helps-image-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/img/AboutUs-section.png`}
                  alt="BlueBox for Business"
                  className="business-helps-image"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="business-helps-content">
                <h2 className="business-helps-title">How BlueBox for Business Helps Businesses</h2>
                <p className="business-helps-text">
                  BlueBox helps professionals run cleaner, faster, more polished projects by replacing messy cardboard with uniform, stackable crates, scheduled drop-off/ pickup, and business-friendly pricing.
                </p>
                <p className="business-helps-text">
                  The result is tighter operations, fewer damaged items, and sites that look organized and premium in front of clients and residents.
                </p>
                <p className="business-helps-text">
                  Partners also gain sustainability cred by cutting cardboard waste— plus preferred rates, priority service, business referrals and several other perks
                </p>
                <button
                  className="btn business-partner-btn"
                  onClick={scrollToForm}>
                  Become a Partner
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* How BlueBox Helps Businesses Section End */}

      {/* Our Brand Promise Section */}
      <section className="business-brand-promise-section">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h2 className="business-brand-promise-title">Our Brand Promise</h2>
              <p className="business-brand-promise-text">
                BlueBox for Business is the cleaner, smarter alternative to cardboard—durable, reusable crates with fast drop-off/pickup and partner-friendly pricing. Keep projects tidy, cut waste, and move faster while presenting a polished, professional image that clients notice and trust. Eco-forward, efficient, and built to make your business look its best.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Our Brand Promise Section End */}

      {/* Who is it for Section */}
      <section className="business-who-section">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h2 className="business-who-title">Who is it for?</h2>
            </div>
          </div>
          <div className="business-who-cards">
            {/* Card 1 - Real Estate Agents */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/real-estate.png`}
                    alt="Real Estate Agents"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Real Estate Agents & Home Stagers</h3>
                  <p className="business-who-card-text">
                    Who refer BlueBox services to clients enabling them to move with less stress.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 - Professional Organizers */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/professional.png`}
                    alt="Professional Organizers"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Professional Organizers</h3>
                  <p className="business-who-card-text">
                    Who rely on sturdy, reusable crates to keep projects streamlined and photo-ready.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3 - Contractors & Remodelers */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/contractors.png`}
                    alt="Contractors & Remodelers"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Contractors & Remodelers</h3>
                  <p className="business-who-card-text">
                    Who protect client belongings and keep job sites clean throughout renovations.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 4 - Property Managers & HOAs */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/property.png`}
                    alt="Property Managers"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Property Managers & HOAs</h3>
                  <p className="business-who-card-text">
                    Who reduce cardboard blight during move-ins /outs and improve resident experience.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 5 - Movers & Relocation Services */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/movers.png`}
                    alt="Movers & Relocation Services"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Movers & Relocation Services</h3>
                  <p className="business-who-card-text">
                    Who offer a cleaner, faster client experience by using reusable crates and cutting cardboard waste.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 6 - Event & Production Teams */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/production.png`}
                    alt="Event & Production Teams"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Event & Production Teams</h3>
                  <p className="business-who-card-text">
                    Who move gear, décor, and inventory efficiently and sustainably.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 7 - E-Commerce Management */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/e-commerce.png`}
                    alt="Event & Production Teams"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">E-commerce Inventory Management</h3>
                  <p className="business-who-card-text">
                    For small online sellers needing short-term, portable storage.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 8 - Disaster Restoration */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/diesaster.png`}
                    alt="Event & Production Teams"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Disaster Restoration/Insurance Claims</h3>
                  <p className="business-who-card-text">
                    Help families pack and protect belongings after a fire, earthquake, or any other natural disaster.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 9 - Downsizing/Estate */}
            <div className="business-who-card-wrapper">
              <div className="business-who-card">
                <div className="business-who-card-image-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/estate.png`}
                    alt="Event & Production Teams"
                    className="business-who-card-image"
                  />
                </div>
                <div className="business-who-card-content">
                  <h3 className="business-who-card-title">Senior Downsizing/Estate Sales</h3>
                  <p className="business-who-card-text">
                    Packing and sorting during transitions to assisted living or family handoffs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Who is it for Section End */}

      {/* Partners will receive Section */}
      <section className="business-partners-section">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h2 className="business-partners-title">Partners will receive</h2>
            </div>
          </div>
          <div className="business-partners-cards">
            {/* Card 1 - Business referrals */}
            <div className="business-partners-card">
              <div className="business-partners-icon-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/img/asset8.png`}
                  alt="Business referrals"
                  className="business-partners-icon"
                />
              </div>
              <p className="business-partners-card-text">
                Business referrals, <span className="text-blue">a profile and direct backlink</span> from our site to theirs
              </p>
            </div>

            {/* Card 2 - Access to exclusive */}
            <div className="business-partners-card">
              <div className="business-partners-icon-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/img/asset12.png`}
                  alt="Exclusive pricing"
                  className="business-partners-icon"
                />
              </div>
              <p className="business-partners-card-text">
                Access to <span className="text-blue">exclusive BlueBox for Business pricing & kits</span>
              </p>
            </div>

            {/* Card 3 - Optional co-branded */}
            <div className="business-partners-card">
              <div className="business-partners-icon-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/img/asset9.png`}
                  alt="Co-branded materials"
                  className="business-partners-icon"
                />
              </div>
              <p className="business-partners-card-text">
                Optional <span className="text-blue">co-branded materials</span> to share with their clients
              </p>
            </div>

            {/* Card 4 - Priority support */}
            <div className="business-partners-card">
              <div className="business-partners-icon-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/img/asset5.png`}
                  alt="Priority support"
                  className="business-partners-icon"
                />
              </div>
              <p className="business-partners-card-text">
                <span className="text-blue">Priority support</span> to keep their projects and residents running smoothly
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Partners will receive Section End */}

      {/* Join the Blue Move Section */}
      <section>
        <div className="container">
          <div className="business-join-blue-section">
            <div className="row align-items-end">
              <div className="col-md-9">
                <h2 className="business-join-blue-title">
                  Join the <span className="text-blue">Blue</span> Move
                </h2>
                <p className="business-join-blue-text">
                  Interested in being listed as a Recommended Partner? Fill out our BlueBox for Business partner form and we&apos;ll follow up with details.
                </p>
              </div>
              <div className="col-md-3">
                <button
                  className="btn business-join-blue-btn w-100"
                  onClick={scrollToForm}>
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Join the Blue Move Section End */}

      {/* Why Choose BlueBox for Business Section */}
      <section className="business-why-choose-section">
        <div className="container">
          <div className="row">
            <div className="col-md-12 text-center">
              <h2 className="business-why-choose-title">Why Choose BlueBox for Business?</h2>
            </div>
          </div>
          <div className="business-why-choose-cards">
            {/* Row 1 */}
            <div className="business-why-choose-row">
              {/* Card 1 - Professional, Uniform Look */}
              <div className="business-why-choose-card">
                <div className="business-why-choose-icon-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/asset6.png`}
                    alt="Professional Look"
                    className="business-why-choose-icon"
                  />
                </div>
                <h3 className="business-why-choose-card-title">Professional, Uniform Look</h3>
                <p className="business-why-choose-card-text">
                  Clean, matching boxes keep job sites, events, and properties looking organized—not like a pile of broken cardboard.
                </p>
              </div>

              {/* Card 2 - Stronger, Safer Storage */}
              <div className="business-why-choose-card">
                <div className="business-why-choose-icon-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/asset14.png`}
                    alt="Stronger Storage"
                    className="business-why-choose-icon"
                  />
                </div>
                <h3 className="business-why-choose-card-title">Stronger, Safer Storage</h3>
                <p className="business-why-choose-card-text">
                  Industrial-grade, stackable crates protect gear, inventory, files, and client belongings far better than flimsy boxes.
                </p>
              </div>

              {/* Card 3 - Fast Delivery & Pickup */}
              <div className="business-why-choose-card">
                <div className="business-why-choose-icon-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/asset11.png`}
                    alt="Fast Delivery"
                    className="business-why-choose-icon"
                  />
                </div>
                <h3 className="business-why-choose-card-title">Fast Delivery & Pickup</h3>
                <p className="business-why-choose-card-text">
                  We drop off where you need them, when you need them—and pick them up when you&apos;re done. No box waste, no hauling.
                </p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="business-why-choose-row">
              {/* Card 4 - Flexible Business-Friendly Plans */}
              <div className="business-why-choose-card">
                <div className="business-why-choose-icon-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/asset7.png`}
                    alt="Flexible Plans"
                    className="business-why-choose-icon"
                  />
                </div>
                <h3 className="business-why-choose-card-title">Flexible Business-Friendly Plans</h3>
                <p className="business-why-choose-card-text">
                  Packages built for contractors, property managers, event pros, and offices—with rental lengths that match real project timelines.
                </p>
              </div>

              {/* Card 5 - Trackable & Efficient */}
              <div className="business-why-choose-card">
                <div className="business-why-choose-icon-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/asset10.png`}
                    alt="Trackable"
                    className="business-why-choose-icon"
                  />
                </div>
                <h3 className="business-why-choose-card-title">Trackable & Efficient</h3>
                <p className="business-why-choose-card-text">
                  Easy labeling and stackability streamline moves, turns, and setups so your team spends less time wrangling boxes.
                </p>
              </div>

              {/* Card 6 - Sustainable & On-Brand */}
              <div className="business-why-choose-card">
                <div className="business-why-choose-icon-wrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/img/asset13.png`}
                    alt="Sustainable"
                    className="business-why-choose-icon"
                  />
                </div>
                <h3 className="business-why-choose-card-title">Sustainable & On-Brand</h3>
                <p className="business-why-choose-card-text">
                  Reusable boxes cut cardboard waste and align your business with smarter, greener operations your clients notice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Why Choose BlueBox for Business Section End */}

      {/* Testimonials Section */}
      <section className="sliderSection mb-0">
        <div className="container">
          <div className="row">
            <div className="col-md-4">
              <div className="sliderSection-heading">
                <h5 className="sliderSection-title">{`Don't take it from us...`}</h5>
                <h2 className="sliderSection-subTitle">
                  {`Here's what our`}
                  <br /> partners are
                  <br /> saying
                </h2>
              </div>
            </div>
            <div className="col-md-8">
              {/* <div className="slider"> */}
                {console.log("Business Testimonials Count:", testimonials?.results?.length)}
                {testimonials.results && testimonials.results.length > 0 && (
                  <SimpleSlider testimonials={testimonials} />
                )}
              {/* </div> */}
            </div>
          </div>
        </div>
      </section>
      {/* Testimonials Section End */}

      {/* Partner Form Section */}
      <section className="business-partner-form-section">
        <div className="container">
          <div className="business-partner-form-wrapper">
            <h2 className="business-partner-form-title">Partner with BlueBox for Business</h2>
            <p className="business-partner-form-description">
              Let&apos;s team up to give your clients and your business a cleaner, smarter alternative to cardboard. Share a few details below and we&apos;ll follow up with a custom partner kit, pricing, and promo assets tailored to your business.
            </p>
            <Form
              form={form}
              layout="vertical"
              onFinish={handlePartnerSubmit}
              className="business-partner-form">
              <div className="row">
                <div className="col-md-6">
                  <Form.Item
                    name="firstName"
                    label="First name"
                    rules={[{ required: true, message: 'Please enter your first name' }]}>
                    <Input placeholder="Sofia" size="large" />
                  </Form.Item>
                </div>
                <div className="col-md-6">
                  <Form.Item
                    name="lastName"
                    label="Last name"
                    rules={[{ required: true, message: 'Please enter your last name' }]}>
                    <Input placeholder="Test" size="large" />
                  </Form.Item>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Form.Item
                    name="companyName"
                    label="Company name"
                    rules={[{ required: true, message: 'Please enter your company name' }]}>
                    <Input placeholder="BlueBox" size="large" />
                  </Form.Item>
                </div>
                <div className="col-md-6">
                  <Form.Item
                    name="industryType"
                    label="Industry type"
                    rules={[{ required: true, message: 'Please enter your industry type' }]}>
                    <Input placeholder="Movers" size="large" />
                  </Form.Item>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <Form.Item
                    name="email"
                    label="E-mail Adress"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}>
                    <Input placeholder="sofia@yopmail.com" size="large" />
                  </Form.Item>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 text-right">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="business-partner-form-submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </section>
      {/* Partner Form Section End */}

      {/* FAQ Section */}
      <section className="business-faq-section">
        <div className="container">
          <div className="row">
            <div className="col-md-5">
              <h2 className="business-faq-title">Frequently Asked Questions</h2>
              <div className="business-faq-list">
                {/* FAQ 1 */}
                <div className="business-faq-item">
                  <div
                    className="business-faq-question"
                    onClick={() => toggleFAQ('1')}>
                    <h2>Who is the BlueBox for Business program for?</h2>
                    {activeKey === '1' ? (
                      <MinusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    ) : (
                      <PlusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    )}
                  </div>
                  {activeKey === '1' && (
                    <div className="business-faq-answer-wrapper">
                      <p className="business-faq-answer">
                        Contractors/remodelers, property managers/HOAs, realtors & stagers, event/
                        production teams, organizers, and movers/relocation services.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ 2 */}
                <div className="business-faq-item">
                  <div
                    className="business-faq-question"
                    onClick={() => toggleFAQ('2')}>
                    <h2>How do delivery and pickup work?</h2>
                    {activeKey === '2' ? (
                      <MinusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    ) : (
                      <PlusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    )}
                  </div>
                  {activeKey === '2' && (
                    <div className="business-faq-answer-wrapper">
                      <p className="business-faq-answer">
                        We deliver BlueBoxes directly to your job site or client location on your
                        schedule, and pick them up when you&apos;re done. No hauling, no hassle.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ 3 */}
                <div className="business-faq-item">
                  <div
                    className="business-faq-question"
                    onClick={() => toggleFAQ('3')}>
                    <h2>What about pricing and partner benefits?</h2>
                    {activeKey === '3' ? (
                      <MinusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    ) : (
                      <PlusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    )}
                  </div>
                  {activeKey === '3' && (
                    <div className="business-faq-answer-wrapper">
                      <p className="business-faq-answer">
                        Partners receive exclusive pricing, business referrals, co-branded materials,
                        and priority support. Contact us to learn more about custom rates for your
                        business.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ 4 */}
                <div className="business-faq-item">
                  <div
                    className="business-faq-question"
                    onClick={() => toggleFAQ('4')}>
                    <h2>Are the boxes clean and durable?</h2>
                    {activeKey === '4' ? (
                      <MinusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    ) : (
                      <PlusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    )}
                  </div>
                  {activeKey === '4' && (
                    <div className="business-faq-answer-wrapper">
                      <p className="business-faq-answer">
                        Yes! Our boxes are industrial-grade, sanitized between uses, and built to last
                        400+ moves. They&apos;re stronger and more reliable than cardboard.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ 5 */}
                <div className="business-faq-item">
                  <div
                    className="business-faq-question"
                    onClick={() => toggleFAQ('5')}>
                    <h2>Is there a minimum order or service area?</h2>
                    {activeKey === '5' ? (
                      <MinusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    ) : (
                      <PlusOutlined style={{ color: '#0080FF', fontSize: '18px' }} />
                    )}
                  </div>
                  {activeKey === '5' && (
                    <div className="business-faq-answer-wrapper">
                      <p className="business-faq-answer">
                        Minimums and service areas vary by location and partnership tier. Reach out to
                        discuss your specific needs and we&apos;ll work with you.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-7 d-flex align-items-center justify-content-center">
              <div className="business-faq-image-wrapper">
                <img
                  src={`${process.env.PUBLIC_URL}/img/new-boxes-desktop1.png`}
                  alt="BlueBox Crate"
                  className="business-faq-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ Section End */}

    </>
  );
}

Business.propTypes = {
  setFooteroffice: PropTypes.func,
  setshowHideFooter: PropTypes.func,
  showHideinnerFooter: PropTypes.any
};